import React from "react";
import { api } from "@/lib/config";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Loader2, RotateCcw, Save } from "lucide-react";

const DEFAULTS = {
  company_name: "JFeva Solutions, LLC",
  owner_name: "Corwin Calloway",
  owner_title: "Owner",
  tagline: "Flooring & Tile · Florida",
  motto: "Our quality will floor you.",
  phone: "850-252-0162",
  email: "info@jfeva.com",
  service_area: "Florida & surrounding areas",
  website: "https://jfeva.com",
};

const FIELDS = [
  { key: "company_name", label: "Company name", full: true },
  { key: "owner_name", label: "Owner name" },
  { key: "owner_title", label: "Owner title / role" },
  { key: "tagline", label: "Tagline (small line under company name)", full: true },
  { key: "motto", label: "Motto (shown in quotes)", full: true },
  { key: "phone", label: "Phone" },
  { key: "email", label: "Email" },
  { key: "service_area", label: "Service area", full: true },
  { key: "website", label: "Website (used by QR code)", full: true },
];

export default function CardSettings() {
  const [form, setForm] = React.useState(DEFAULTS);
  const [loading, setLoading] = React.useState(true);
  const [saving, setSaving] = React.useState(false);
  const [tick, setTick] = React.useState(0); // cache-buster for preview

  const load = async () => {
    try {
      const r = await api.get("/card-settings");
      setForm({ ...DEFAULTS, ...r.data });
    } catch {
      toast.error("Failed to load settings");
    } finally { setLoading(false); }
  };

  React.useEffect(() => { load(); }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.put("/admin/card-settings", form);
      toast.success("Business card updated.");
      setTick((t) => t + 1);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed.");
    } finally { setSaving(false); }
  };

  const resetDefaults = () => {
    if (!window.confirm("Reset all card fields to defaults?")) return;
    setForm(DEFAULTS);
  };

  if (loading) return <div className="text-sm text-[color:var(--brand-woodDark)]">Loading card settings...</div>;

  return (
    <div data-testid="admin-card-settings" className="grid gap-8 lg:grid-cols-12">
      <form onSubmit={save} className="lg:col-span-7 rounded-2xl border border-black/10 bg-white p-6 space-y-5" data-testid="card-settings-form">
        <div className="flex items-center justify-between">
          <div>
            <div className="eyebrow text-[color:var(--brand-wood)]">Business card</div>
            <h3 className="mt-1 font-display text-2xl">Edit content</h3>
          </div>
          <button type="button" onClick={resetDefaults} data-testid="card-reset-defaults"
            className="inline-flex items-center gap-2 text-xs text-[color:var(--brand-woodDark)] hover:text-[color:var(--brand-ink)]">
            <RotateCcw className="h-3.5 w-3.5"/> Reset defaults
          </button>
        </div>

        <div className="grid grid-cols-2 gap-4">
          {FIELDS.map((f) => (
            <div key={f.key} className={f.full ? "col-span-2" : ""}>
              <Label className="text-xs uppercase tracking-widest text-[color:var(--brand-woodDark)]">{f.label}</Label>
              {f.key === "motto" ? (
                <Textarea
                  data-testid={`card-input-${f.key}`}
                  rows={2}
                  value={form[f.key] || ""}
                  onChange={(e) => set(f.key, e.target.value)}
                  className="mt-2"
                />
              ) : (
                <Input
                  data-testid={`card-input-${f.key}`}
                  value={form[f.key] || ""}
                  onChange={(e) => set(f.key, e.target.value)}
                  className="mt-2"
                />
              )}
            </div>
          ))}
        </div>

        <div className="flex items-center justify-end gap-3 pt-2">
          <button type="submit" disabled={saving} data-testid="card-save"
            className="inline-flex items-center gap-2 rounded-full bg-[color:var(--brand-ink)] text-[color:var(--brand-cream)] px-6 py-3 text-sm disabled:opacity-60 hover:-translate-y-0.5 transition-transform">
            {saving ? <Loader2 className="h-4 w-4 animate-spin"/> : <Save className="h-4 w-4"/>}
            Save changes
          </button>
        </div>
        <p className="text-xs text-[color:var(--brand-woodDark)]">
          Changes update the public /card page, the downloadable vCard, and the printable PDF cards.
        </p>
      </form>

      <div className="lg:col-span-5 space-y-4" data-testid="card-preview">
        <div className="eyebrow text-[color:var(--brand-wood)]">Live preview</div>
        <div className="rounded-2xl bg-[color:var(--brand-ink)] text-[color:var(--brand-cream)] p-6 aspect-[7/4] shadow-xl relative overflow-hidden"
          style={{
            backgroundImage:
              "radial-gradient(circle at 85% 15%, rgba(140,113,90,0.25), transparent 45%), radial-gradient(circle at 15% 85%, rgba(74,93,82,0.18), transparent 50%)",
          }}
        >
          <div className="absolute inset-3 border border-white/15 rounded-xl pointer-events-none"/>
          <div className="relative h-full flex flex-col">
            <div className="flex items-start gap-4">
              <img src={`/api/logo.png?t=${tick}`} alt="" className="h-16 w-16 object-contain"/>
              <div className="min-w-0 flex-1">
                <div className="font-display text-lg leading-tight break-words">{form.company_name}</div>
                <div className="text-[10px] tracking-[0.2em] uppercase text-[color:var(--brand-wood)] mt-1 truncate">{form.tagline}</div>
                <div className="text-xs font-medium mt-2 truncate">{form.owner_name}</div>
                <div className="text-[10px] tracking-[0.2em] uppercase text-white/60 truncate">{form.owner_title}</div>
              </div>
            </div>
            <div className="flex-1 flex items-center justify-center px-2">
              <p className="font-display italic text-sm text-white/95 text-center line-clamp-2">&ldquo;{form.motto}&rdquo;</p>
            </div>
            <div className="flex items-end justify-between text-[10px]">
              <div className="min-w-0">
                <div className="font-medium truncate">{form.phone}</div>
                <div className="opacity-85 truncate">{form.email}</div>
              </div>
              <div className="text-right opacity-75 leading-tight max-w-[45%] truncate">
                {form.service_area}
              </div>
            </div>
          </div>
        </div>
        <p className="text-[11px] text-[color:var(--brand-woodDark)]">
          Preview reflects unsaved edits. Click <span className="font-medium">Save changes</span> to publish.
        </p>
      </div>
    </div>
  );
}
